/**
 * Issues an invitation and, once issued, shows its link exactly once
 * (§18.2).
 *
 * The platform sends no email, no SMS and no notification of any kind — the
 * administrator reading this screen is personally the delivery mechanism,
 * and it says so before the link is generated (`invite.delivery.before`) as
 * well as after (`invite.issued.send`). The 201 body carries the link once;
 * no route ever returns it again, so a person who navigates away without
 * copying it has to revoke and reissue rather than come back for it.
 *
 * `ADDRESS_READER` is never offered here (§5.1): `RolePicker` (`invite` mode)
 * renders it as a disabled row with the reason stated alongside it, rather
 * than omitting it, because an option that just isn't there reads as a bug
 * report waiting to happen -- see `RolePicker`'s own doc comment for why
 * that explanation lives there and not in this form.
 *
 * The `invite.*` catalogue keys are a distinct section from the catalogue's
 * older `invitations.create.*`/`invitations.delivery.*` keys, which are not
 * read by this screen -- see the catalogue's own module comment.
 *
 * `FormDialog`'s footer button is one control, so the issued state reuses it
 * as `invite.another` rather than adding a second primary action: submitting
 * the form while a link is already showing resets it for the next address,
 * exactly what that label promises.
 *
 * A 409 here (`invite.duplicate`) never names a field (`ApiFailure`'s
 * `conflict` member carries only `message`), so `fieldErrorOf` cannot route
 * it to the email input the way a real 422 would -- it is translated into a
 * fieldless failure and shown as the dialog's one general failure instead.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import type { ApiFailure } from '../../../lib/http/errors';
import { FormDialog } from '../../../chrome/form';
import { Button } from '../../../ui/button';
import { Field, FieldDescription, FieldLabel } from '../../../ui/field';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { useCampaigns } from '../home/useCampaigns';
import { INVITABLE_ROLES } from '../api/schemas';
import { RolePicker } from '../members/RolePicker';
import { tt } from '../i18n';
import { tm } from '../pages/team.messages';
import { useIssueInvitation } from './useInvitations';

type InvitableRole = (typeof INVITABLE_ROLES)[number];

const TENANT_WIDE = 'tenant-wide';

export function InviteDialog(props: { tenantId: string; trigger: ReactNode }): ReactNode {
  const { tenantId, trigger } = props;
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitableRole>('CAMPAIGN_ANALYST');
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const issue = useIssueInvitation(tenantId);
  const campaigns = useCampaigns(tenantId);

  function reset(): void {
    issue.reset();
    setEmail('');
    setRole('CAMPAIGN_ANALYST');
    setCampaignId(null);
  }

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) reset();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (issue.isSuccess) {
      reset();
      return;
    }
    issue.mutate({ email, role, campaign_id: campaignId });
  }

  const rawFailure: ApiFailure | null = issue.error?.failure ?? null;
  const failure: ApiFailure | null =
    rawFailure?.kind === 'conflict'
      ? { kind: 'invalid', field: null, message: tt('invite.duplicate') }
      : rawFailure;

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      trigger={trigger}
      title={tt('invite.submit')}
      description={issue.isSuccess ? undefined : tt('invite.delivery.before')}
      submitLabel={issue.isSuccess ? tt('invite.another') : tt('invite.submit')}
      pending={issue.isPending}
      failure={failure}
      onSubmit={handleSubmit}
    >
      {issue.isSuccess ? (
        <div data-testid="invite-issued" className="flex flex-col gap-3">
          <p>{tt('invite.issued.title')}</p>
          <p>{tt('invite.issued.once')}</p>
          <div className="flex items-center gap-2">
            {/* A text node, not a form control's `value`: an `<input>`'s value
                is a DOM property React sets directly, which never reflects into
                the `value` *attribute* -- so it would not appear in serialised
                HTML at all, let alone be countable there. The link is a bearer
                credential; it must render in exactly one place, and that place
                has to be one `innerHTML` can actually see it in. */}
            <code
              data-testid="invitation-link"
              className="flex-1 truncate rounded border px-2 py-1 text-sm"
            >
              {issue.data.link}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void navigator.clipboard?.writeText(issue.data.link);
              }}
            >
              {tt('invite.copy')}
            </Button>
          </div>
          <p className="text-body-sm text-muted-foreground">
            {tt('invite.issued.send', { email: issue.data.email })}
          </p>
        </div>
      ) : (
        <>
          <p className="text-body-sm text-muted-foreground">{tt('invite.email.hint')}</p>
          <Field>
            <FieldLabel htmlFor="invite-email">{tt('invitations.create.emailLabel')}</FieldLabel>
            <Input
              id="invite-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <div className="flex flex-col gap-1.5">
            <Label>{tt('invitations.create.roleLabel')}</Label>
            <RolePicker
              purpose="invite"
              value={role}
              onChange={(next) => setRole(next as InvitableRole)}
            />
            <FieldDescription>{tt('invitations.create.roleHint')}</FieldDescription>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-scope">{tm('invite.scope.label')}</Label>
            <Select
              value={campaignId ?? TENANT_WIDE}
              onValueChange={(next) => setCampaignId(next === TENANT_WIDE ? null : next)}
            >
              <SelectTrigger id="invite-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TENANT_WIDE}>{tm('invite.scope.tenantWide')}</SelectItem>
                {(campaigns.data ?? []).map((campaign) => (
                  <SelectItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      )}
    </FormDialog>
  );
}
